import { useCallback } from 'react';
import { FaRegTrashAlt } from 'react-icons/fa';

import { AgreementFileReadModel, useLazyGetAgreementsByAgreementIdFilesAndFileIdDownloadQuery } from '@client/api';
import { Button, isDefinedString, TableCell, TableRow, Text } from '@client/toolkit';
import { useAgreementPermissions } from '../../../../../hooks/useAgreementPermissions/useAgreementPermissions';
import { PermissionKeys } from '@client/api';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

type AgreementFileItemProps = {
  agreementFile?: AgreementFileReadModel;
  isFetching?: boolean;
  onClickDelete?: (usage: AgreementFileReadModel) => void;
};

export function AgreementFileItem({ agreementFile, isFetching, onClickDelete }: AgreementFileItemProps) {
  const { t } = useTranslation('notification');
  const { hasPermission } = useAgreementPermissions({ id: agreementFile?.agreementId ?? '' });
  const [downloadFile] = useLazyGetAgreementsByAgreementIdFilesAndFileIdDownloadQuery();

  const handleDownload = useCallback(async () => {
    if (agreementFile == null) return;

    try {
      const { data } = await downloadFile({
        agreementId: agreementFile.agreementId,
        fileId: agreementFile.id,
      });

      if (data instanceof Blob) {
        const blobUrl = window.URL.createObjectURL(data);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = agreementFile?.file?.name;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      toast.error(t('DOWNLOAD_FAILED'));
    }
  }, [agreementFile, downloadFile]);

  return (
    <TableRow verticalAlign="middle">
      <TableCell loading={isFetching}>
        <div className="flex items-center justify-end">
          <div className="flex-1 cursor-pointer" onClick={handleDownload}>
            <Text as="div" isEmpty={!isDefinedString(agreementFile?.file?.name)}>
              <strong>{agreementFile?.file?.name}</strong>
            </Text>
          </div>
          {hasPermission([PermissionKeys.Agreement.Update]) && (
            <Button
              className="ml-2 text-brand-primary bg-surface-elevated size-9"
              size="xs"
              color="custom"
              disabled={typeof onClickDelete !== 'function' || isFetching}
              onClick={() => {
                if (typeof onClickDelete !== 'function') return;
                if (agreementFile == null) return;
                onClickDelete(agreementFile);
              }}
            >
              <FaRegTrashAlt />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
